import React, { useState } from 'react';
import { Button } from 'antd';

import {
  CustomerDraftResponse,
  CustomerEditResponse,
  ResourceDownloadResponse,
  ServiceAgreementTypeEnum,
} from 'api/backofficeApiClient';
import ClientModal from 'components/modals/ClientModal';
import EditClientAdditionalInfoModal from 'components/modals/EditClientAdditionalInfoModal';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import {
  getCertificateFileInfoForCustomer,
  getCustomerOnboardingDocumentationFile,
  getCustomerTempToPermConversionFileInfoForCustomer,
  getEditAdditionalInfoForCustomer,
  getEditDraftInfoForCustomer,
  getEditInfoForCustomerByAdmin,
  getServiceAgreementFileInfoForCustomer,
  getSOWTemplateFile,
  getEditInfoForCustomer,
  getCustomerPurchaseOrderFile,
} from 'redux/reducers/clientSlice';
import {
  mapCustomerAdditionalDetailsToInitialValues,
  mapCustomerDetailsToInitialValues,
} from './utils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BaseAlert from 'components/wrappers/BaseAlert';
import {
  CustomerAdditionalInfoResponseModel,
  ServiceAgreementInfoModel,
} from 'api/adminApiClient';
import { ClientDetailSection } from '../enums';
import { generateUploadFileList } from 'utils/fileUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { ServiceAgreementFileInfoType } from 'components/modals/ClientModal/types';
import { ClientCreationEnum } from 'components/modals/ClientModal/enum';

type Props = {
  customerId: string;
  isDraft: boolean;
  activeSection: ClientDetailSection;
  onSubmit: () => void;
};

const successStatus = 'fulfilled';

export default function EditClient(props: Props) {
  const { customerId, activeSection, onSubmit, isDraft } = props;
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);
  const [
    isEditAdditionalInfoModalVisible,
    setIsEditAdditionalInfoModalVisible,
  ] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editCustomerInfo, setEditCustomerInfo] = useState<
    CustomerDraftResponse | CustomerEditResponse
  >();
  const [editCustomerAdditionalInfo, setEditCustomerAdditionalInfo] =
    useState<CustomerAdditionalInfoResponseModel>();
  const [serviceAgreementFileInfo, setServiceAgreementFileInfo] =
    useState<ServiceAgreementFileInfoType>();
  const [certificateFileInfo, setCertificateFileInfo] =
    useState<ResourceDownloadResponse>();
  const [sowTemplateFileInfo, setSowTemplateFileInfo] =
    useState<ResourceDownloadResponse>();
  const [onboardingDocumentationFileInfo, setOnboardingDocumentationFileInfo] =
    useState<ResourceDownloadResponse[]>([]);
  const [tempToPermConversionFileInfo, setTempToPermConversionFileInfo] =
    useState<ResourceDownloadResponse>();
  const [purchaseOrderFile, setPurchaseOrderFile] =
    useState<ResourceDownloadResponse>();

  const creationType = isDraft
    ? ClientCreationEnum.EditDraft
    : ClientCreationEnum.Edit;
  const isDraftAvailable = !isAdminRole && isDraft;

  const handleEditClick = async () => {
    if (isLoading) return;

    setIsLoading(true);

    switch (activeSection) {
      case ClientDetailSection.AdditionalInfo:
        await onAdditionalInfoEdit();
        break;

      case ClientDetailSection.PrimaryInfo:
      default:
        await onMainInfoEdit();
        break;
    }

    setIsLoading(false);
  };

  const handleEditModalClose = () => {
    // Reset file info
    setServiceAgreementFileInfo(undefined);

    setIsEditModalVisible(false);
  };

  const handleEditAdditionalInfoModalClose = () => {
    // Reset files info
    setTempToPermConversionFileInfo(undefined);
    setOnboardingDocumentationFileInfo([]);
    setSowTemplateFileInfo(undefined);
    setCertificateFileInfo(undefined);
    setPurchaseOrderFile(undefined);

    setIsEditAdditionalInfoModalVisible(false);
  };

  const getCorrectAction = () => {
    switch (creationType) {
      case ClientCreationEnum.EditDraft:
        return getEditDraftInfoForCustomer;
      case ClientCreationEnum.Edit:
        return isAdminRole
          ? getEditInfoForCustomerByAdmin
          : getEditInfoForCustomer;
    }
  };

  const getAgreementDetails = (
    data: CustomerDraftResponse | CustomerEditResponse,
  ): ServiceAgreementInfoModel => {
    switch (creationType) {
      case ClientCreationEnum.Edit: {
        return (data as CustomerEditResponse)?.customerDocument
          ?.agreementDetailsInfo;
      }
      case ClientCreationEnum.EditDraft:
        return (data as CustomerDraftResponse)?.agreementDetailsInfo;
    }
  };

  const onMainInfoEdit = async () => {
    const correctAction: any = getCorrectAction();

    const result = await dispatch(correctAction(customerId));

    if (correctAction.fulfilled.match(result)) {
      const agreementDetails = getAgreementDetails(result.payload);

      if (agreementDetails?.agreementDetailsFileKey) {
        await loadAgreementInfoFile(
          agreementDetails?.agreementDetailsType as ServiceAgreementTypeEnum,
        );
      }

      setEditCustomerInfo(result.payload);
      setIsEditModalVisible(true);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onAdditionalInfoEdit = async () => {
    const result = await dispatch(getEditAdditionalInfoForCustomer(customerId));

    if (getEditAdditionalInfoForCustomer.fulfilled.match(result)) {
      if (
        result.payload?.generalContractInformation?.insuranceRequirements
          ?.certificateOfInsuranceCreatedFileKey
      ) {
        await loadCertificateInfoFile();
      }

      if (
        result.payload?.financialInformation?.rateOverview
          ?.tempToPermConversionFileKey
      ) {
        await loadTempToPermConversionFile();
      }

      if (
        result.payload?.financialInformation?.mspOrVmsOverview
          ?.purchaseOrderFileKey
      ) {
        await loadPurchaseOrderFile();
      }

      if (
        result.payload.generalContractInformation?.insuranceRequirements
          ?.sowTemplateFileKey
      ) {
        await loadSowTemplateInfoFile();
      }

      if (
        result.payload.additionalInfo3?.assignmentRequirements
          ?.onboardingDocumentationFileKeys
      ) {
        await loadAllOnboardingDocumentationFiles(
          result.payload.additionalInfo3?.assignmentRequirements
            ?.onboardingDocumentationFileKeys,
        );
      }

      setEditCustomerAdditionalInfo(result.payload);
      setIsEditAdditionalInfoModalVisible(true);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadAllOnboardingDocumentationFiles = async (fileKeys: string[]) => {
    const files = (
      await Promise.allSettled(
        fileKeys.map((key) => loadOnboardingDocumentationFile(key)),
      )
    )
      .filter((item) => item.status === successStatus)
      .map((item) => item.value);

    setOnboardingDocumentationFileInfo(files);
  };

  const loadAgreementInfoFile = async (
    serviceAgreement: ServiceAgreementTypeEnum,
  ) => {
    const result = await dispatch(
      getServiceAgreementFileInfoForCustomer({
        customerId,
        isAdminRole: isAdminRole as boolean,
      }),
    );

    if (getServiceAgreementFileInfoForCustomer.fulfilled.match(result)) {
      setServiceAgreementFileInfo({
        type: serviceAgreement,
        files: generateUploadFileList(result.payload),
      });
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadCertificateInfoFile = async () => {
    const result = await dispatch(
      getCertificateFileInfoForCustomer(customerId),
    );

    if (getCertificateFileInfoForCustomer.fulfilled.match(result)) {
      setCertificateFileInfo(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadSowTemplateInfoFile = async () => {
    const result = await dispatch(
      getSOWTemplateFile({ customerId, isAdminRole }),
    );

    if (getSOWTemplateFile.fulfilled.match(result)) {
      setSowTemplateFileInfo(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadOnboardingDocumentationFile = async (fileKey: string) => {
    const result = await dispatch(
      getCustomerOnboardingDocumentationFile({
        customerId,
        fileKey,
        isAdminRole,
      }),
    );

    if (getCustomerOnboardingDocumentationFile.fulfilled.match(result)) {
      return result.payload;
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
      return Promise.reject();
    }
  };

  const loadTempToPermConversionFile = async () => {
    const result = await dispatch(
      getCustomerTempToPermConversionFileInfoForCustomer({
        customerId,
        isAdminRole,
      }),
    );

    if (
      getCustomerTempToPermConversionFileInfoForCustomer.fulfilled.match(result)
    ) {
      setTempToPermConversionFileInfo(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadPurchaseOrderFile = async () => {
    const result = await dispatch(getCustomerPurchaseOrderFile(customerId));

    if (getCustomerPurchaseOrderFile.fulfilled.match(result)) {
      setPurchaseOrderFile(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const getEditButtonLabel = () => {
    switch (activeSection) {
      case ClientDetailSection.AdditionalInfo:
        return 'Edit additional info';

      case ClientDetailSection.PrimaryInfo:
      default:
        return 'Edit';
    }
  };

  const getCustomerModal = () => {
    const initialValues = mapCustomerDetailsToInitialValues(
      editCustomerInfo as CustomerDraftResponse | CustomerEditResponse,
      creationType,
    );

    return (
      <ClientModal
        onSubmit={onSubmit}
        onClose={handleEditModalClose}
        creationType={creationType}
        initialValues={initialValues}
        serviceAgreementFileInfo={serviceAgreementFileInfo}
        onChangeServiceAgreementFileInfo={setServiceAgreementFileInfo}
        customerId={editCustomerInfo?.customerId as string}
        isDraftAvailable={isDraftAvailable}
      />
    );
  };

  const getCustomerEditAdditionalInfoModal = () => {
    const initialValues = mapCustomerAdditionalDetailsToInitialValues(
      editCustomerAdditionalInfo as CustomerAdditionalInfoResponseModel,
    );

    return (
      <EditClientAdditionalInfoModal
        customerId={editCustomerAdditionalInfo?.customerId as string}
        visible={isEditAdditionalInfoModalVisible}
        initialValues={initialValues}
        certificateFileInfo={certificateFileInfo}
        sowTemplateFileInfo={sowTemplateFileInfo}
        onboardingDocumentationFileInfo={onboardingDocumentationFileInfo}
        tempToPermConversionFileInfo={tempToPermConversionFileInfo}
        purchaseOrderFile={purchaseOrderFile}
        onSubmit={onSubmit}
        onClose={handleEditAdditionalInfoModalClose}
      />
    );
  };

  return (
    <>
      <Button
        className="with-icon"
        onClick={handleEditClick}
        loading={isLoading}
        icon={<span className="icon icon-edit" />}
      >
        {getEditButtonLabel()}
      </Button>

      {isEditModalVisible && <>{getCustomerModal()}</>}
      {isEditAdditionalInfoModalVisible && getCustomerEditAdditionalInfoModal()}
    </>
  );
}
